import { useLingui } from '@lingui/react/macro'
import { classifyCadence, type Cadence, type Season } from '@markku/utils'
import type { Service } from './utils'

/**
 * Localized cadence presentation for the resident app (i18n: en/fi/es). The
 * cadence is classified once in @markku/utils (shared with backend/ERP); this
 * hook only renders the descriptor's keys into Lingui strings. When a label is
 * shown in months the exact interval in days is offered as `tooltip`, and
 * `seasonRange` carries the season window for seasonal tasks.
 */
export function useCadence(service: Service): {
  label: string
  tooltip: string | null
  seasonRange: string | null
} {
  const { t } = useLingui()
  const cadence = classifyCadence(service.frequency, service.frequencyDays)
  const days = service.frequencyDays ?? 0

  const everyNDays = (n: number) => t`Every ${n} days`
  const everyNWeeks = (n: number) => t`Every ${n} weeks`
  const everyNMonths = (n: number) => t`Every ${n} months`

  const seasonLabels: Record<Season, string> = {
    spring: t`In spring`,
    summer: t`In summer`,
    fall: t`In autumn`,
    winter: t`In winter`,
  }

  // Render a descriptor to a label; `hidesDays` marks a label that hides the raw
  // day interval (weeks/months), so the caller can offer it on hover.
  const render = (c: Cadence): { label: string; hidesDays: boolean } => {
    switch (c.key) {
      case 'asNeeded':
        return { label: t`As needed`, hidesDays: false }
      case 'raw':
        return { label: c.text, hidesDays: false }
      case 'days':
        return { label: everyNDays(c.n), hidesDays: false }
      case 'weeks':
        return {
          label: c.n === 1 ? t`Weekly` : everyNWeeks(c.n),
          hidesDays: true,
        }
      case 'months':
        return {
          label: c.n === 1 ? t`Monthly` : everyNMonths(c.n),
          hidesDays: true,
        }
      case 'yearly':
        return { label: t`Yearly`, hidesDays: false }
      case 'seasonal': {
        const season = seasonLabels[c.season]
        if (!c.inner) return { label: season, hidesDays: false }
        const inner = render(c.inner)
        return {
          label: `${season} · ${inner.label}`,
          hidesDays: inner.hidesDays,
        }
      }
    }
  }

  const { label, hidesDays } = render(cadence)
  const tooltip = hidesDays && days > 0 ? everyNDays(days) : null

  // Month range for seasonal tasks (reuses existing translated month-range
  // strings), shown in the hover so "In summer" gets a concrete window.
  const seasonRanges: Record<Season, string> = {
    spring: t`March-May`,
    summer: t`June-August`,
    fall: t`September-November`,
    winter: t`December-February`,
  }
  const seasonRange =
    cadence.key === 'seasonal' ? seasonRanges[cadence.season] : null

  return { label, tooltip, seasonRange }
}
